import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, DatePicker, Switch, Button, message, Row, Col } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { customBaseUrl, httpPost } from '../../../store/http';
import { hideLoader, showLoader } from '../../../utils/loader';
import Swal from 'sweetalert2';

const { Option } = Select;
const { TextArea } = Input;

const CreatePricingPlanModal = ({ showModal, hideModal, reloadPricingPlans, productCode }) => {
  const [form] = Form.useForm();
  const [pricingModel, setPricingModel] = useState('FIXED');

  const onFinish = async (values) => {
    showLoader();
    try {
      const payload = {
        ...values,
        productCode: productCode,
        effectiveFrom: values.effectiveFrom ? values.effectiveFrom.toISOString() : undefined,
        effectiveTo: values.effectiveTo ? values.effectiveTo.toISOString() : undefined,
        isActive: values.isActive !== undefined ? values.isActive : true,
        pricingTiers: pricingModel !== 'FIXED' ? values.pricingTiers : undefined,
        pricingRules: pricingModel === 'DYNAMIC' ? values.pricingRules : undefined,
      };

      const response = await httpPost(`${customBaseUrl.integUrl}/api/v1/pricing/products`, payload);
      if (response.status) {
        message.success('Pricing plan created successfully');
        form.resetFields();
        setPricingModel('FIXED'); // Reset pricing model
        hideModal();
        reloadPricingPlans();
      } else {
        const errorMsg = response.data?.description || response.message || 'Failed to create pricing plan';
        Swal.fire('Error', errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error creating pricing plan:', error);
      Swal.fire('Error', 'An error occurred while creating pricing plan', 'error');
    } finally {
      hideLoader();
    }
  };

  return (
    <Modal
      title={`Create Pricing Plan for ${productCode}`}
      open={showModal}
      onCancel={hideModal}
      footer={null}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ isActive: true, currency: 'NGN', pricingModel: 'FIXED', priority: 1 }}
      >
        <Form.Item
          name="pricingName"
          label="Pricing Plan Name"
          rules={[{ required: true, message: 'Please enter pricing plan name' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="pricingDescription"
          label="Description"
          rules={[{ required: true, message: 'Please enter pricing plan description' }]}
        >
          <TextArea rows={2} />
        </Form.Item>
        <Form.Item
          name="pricingModel"
          label="Pricing Model"
          rules={[{ required: true, message: 'Please select a pricing model' }]}
        >
          <Select onChange={setPricingModel}>
            <Option value="FIXED">FIXED</Option>
            <Option value="TIERED">TIERED</Option>
            <Option value="PERCENTAGE">PERCENTAGE</Option>
            <Option value="DYNAMIC">DYNAMIC</Option>
            <Option value="SUBSCRIPTION">SUBSCRIPTION</Option>
            <Option value="USAGE_BASED">USAGE_BASED</Option>
            <Option value="BUNDLE">BUNDLE</Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="basePrice"
          label="Base Price"
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="currency"
          label="Currency"
          rules={[{ required: true, message: 'Please select currency' }]}
        >
          <Select>
            <Option value="NGN">NGN</Option>
            <Option value="USD">USD</Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="minimumAmount"
          label="Minimum Amount"
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="maximumAmount"
          label="Maximum Amount"
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="effectiveFrom"
          label="Effective From"
          rules={[{ required: true, message: 'Please select effective from date' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="effectiveTo"
          label="Effective To"
          rules={[{ required: true, message: 'Please select effective to date' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="isActive" label="Is Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true, message: 'Please enter priority' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        {/* New Metadata Field */}
        <Form.Item
          name="metadata"
          label="Metadata (JSON string)"
          tooltip="Enter metadata as a JSON string, e.g., {'feature': 'premium_support'}"
        >
          <TextArea rows={2} />
        </Form.Item>

        {(pricingModel === 'TIERED' || pricingModel === 'VOLUME_BASED') && (
          <>
            <h3>Pricing Tiers</h3>
            <Form.List name="pricingTiers">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ border: '1px solid #d9d9d9', padding: '16px', marginBottom: '16px', borderRadius: '2px' }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'tierName']}
                            label="Tier Name"
                            rules={[{ required: true, message: 'Missing tier name' }]}
                          >
                            <Input placeholder="Tier Name" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'tierDescription']}
                            label="Tier Description"
                          >
                            <Input placeholder="Tier Description" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'minQuantity']}
                            label="Min Quantity"
                            rules={[{ required: true, message: 'Missing min quantity' }]}
                          >
                            <InputNumber placeholder="Min Quantity" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'maxQuantity']}
                            label="Max Quantity"
                            rules={[{ required: true, message: 'Missing max quantity' }]}
                          >
                            <InputNumber placeholder="Max Quantity" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'minAmount']}
                            label="Min Amount"
                            rules={[{ required: true, message: 'Missing min amount' }]}
                          >
                            <InputNumber placeholder="Min Amount" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'maxAmount']}
                            label="Max Amount"
                            rules={[{ required: true, message: 'Missing max amount' }]}
                          >
                            <InputNumber placeholder="Max Amount" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'unitPrice']}
                            label="Unit Price"
                          >
                            <InputNumber placeholder="Unit Price" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'fixedFee']}
                            label="Fixed Fee"
                          >
                            <InputNumber placeholder="Fixed Fee" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'percentageRate']}
                            label="Percentage Rate"
                          >
                            <InputNumber placeholder="Percentage Rate" min={0} max={1} step={0.01} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'discountPercentage']}
                            label="Discount %"
                          >
                            <InputNumber placeholder="Discount %" min={0} max={100} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'priority']}
                            label="Priority"
                            rules={[{ required: true, message: 'Missing priority' }]}
                          >
                            <InputNumber placeholder="Priority" min={1} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'isActive']}
                            label="Is Active"
                            valuePropName="checked"
                            initialValue={true}
                          >
                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <MinusCircleOutlined onClick={() => remove(name)} style={{ marginTop: '8px' }} />
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Tier
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}

        {(pricingModel === 'DYNAMIC') && (
          <>
            <h3>Pricing Rules</h3>
            <Form.List name="pricingRules">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ border: '1px solid #d9d9d9', padding: '16px', marginBottom: '16px', borderRadius: '2px' }}>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'ruleName']}
                          label="Rule Name"
                          rules={[{ required: true, message: 'Missing rule name' }]}
                        >
                          <Input placeholder="Rule Name" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'ruleDescription']}
                          label="Rule Description"
                        >
                          <Input placeholder="Rule Description" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'ruleType']}
                          label="Rule Type"
                          rules={[{ required: true, message: 'Missing rule type' }]}
                        >
                          <Select placeholder="Rule Type" style={{ width: '100%' }}>
                            <Option value="VOLUME_BASED">VOLUME_BASED</Option>
                            <Option value="TIME_BASED">TIME_BASED</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'conditionField']}
                          label="Condition Field"
                        >
                          <Input placeholder="Condition Field" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'conditionOperator']}
                          label="Condition Operator"
                        >
                          <Select placeholder="Condition Operator" style={{ width: '100%' }}>
                            <Option value="GREATER_THAN">GREATER_THAN</Option>
                            <Option value="LESS_THAN">LESS_THAN</Option>
                            <Option value="EQUALS">EQUALS</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'conditionValue']}
                          label="Condition Value"
                        >
                          <InputNumber placeholder="Condition Value" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'actionType']}
                          label="Action Type"
                        >
                          <Select placeholder="Action Type" style={{ width: '100%' }}>
                            <Option value="DISCOUNT_PERCENTAGE">DISCOUNT_PERCENTAGE</Option>
                            <Option value="FIXED_DISCOUNT">FIXED_DISCOUNT</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'actionValue']}
                          label="Action Value"
                        >
                          <InputNumber placeholder="Action Value" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                       <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'effectiveFrom']}
                          label="Rule Effective From"
                        >
                          <DatePicker showTime placeholder="Rule Effective From" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'effectiveTo']}
                          label="Rule Effective To"
                        >
                          <DatePicker showTime placeholder="Rule Effective To" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'priority']}
                          label="Priority"
                          rules={[{ required: true, message: 'Missing priority' }]}
                        >
                          <InputNumber placeholder="Priority" min={1} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                     <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'isActive']}
                            label="Is Active"
                            valuePropName="checked"
                            initialValue={true}
                          >
                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <MinusCircleOutlined onClick={() => remove(name)} style={{ marginTop: '8px' }}/>
                        </Col>
                     </Row>
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Rule
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Create Pricing Plan
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreatePricingPlanModal;